import { withLayout } from '@/layout/Layout';
import React, { useEffect, useState } from 'react';

import {
  BreadcrumbsProps,
  IProduct,
  IProductInfoList,
  IProductInstructionsItem,
  ISubstance,
  PageProps,
  microMarkupSchemaProps,
} from '@/types/types';
import { LayoutProps } from '@/layout/Layout/Layout.props';
// import { GetStaticPathsContext, GetStaticProps } from 'next';
import { GetStaticPathsContext, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { getApolloClient } from '@/utils/apollo-client';
import { flatListToHierarchical } from '@/utils/flatListToHierarchical';
import { transformMenus } from '@/utils/transformMenus';

import { ROUTES } from '@/utils/routes';
import { ALL_MENU_QUERY, MenuLocationEnum } from '@/data/allMenuQuery';

import { Section } from '@/components/common/Section';
import Image from 'next/image';
import { Heading } from '@/components/typography';

import { useTranslation } from 'next-i18next';

import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { ALL_PRODUCTS_PATH } from '@/data/allProductsPath';
import { PRODUCTS_BY_SLUG_QUERY } from '@/data/productsBySlugQuery';
import { CategoryLabel } from '@/components/common/CategoryLabel';
import Link from 'next/link';
import ArrowIcon from '@/public/icons/arrow-forward.svg';
import { trimText } from '@/utils/trimText';
import { ButtonLink } from '@/components/buttons/ButtonLink';
import { Slider } from '@/components/common/Slider';
import { removeLastTrailingSlash } from '@/utils/removeLastTrailingSlash';
import { DrugCard } from '@/components/cards/DrugCard';
import { filterProductsByCategory } from '@/utils/filterProductsByCategory';
import { ProductGallerySlider } from '@/components/common/ProductGallerySlider';
import { Popup } from '@/components/common/Popup';
import {
  FooterMenuLocationEnum,
  FOOTER_DATA_QUERY,
  FOOTER_DATA_QUERY_EN,
} from '@/data/footerDataQuery';
import { footerDataTransformer } from '@/utils/footerDataTransformer';
import { PRODUCT_SLUG_TRANSLATION } from '@/data/productSlugTranslation';
import { PageHead } from '@/components/common/PageHead';
import { PRODUCT_HEALTHCARE_DOCUMENTS_BY_SLUG } from '@/data/productHealthcareDocuments';

const slugsTerapevtGroup = [
  'neurology',
  'cardiology',
  'andrology-and-gastroenterology',
  'traumatology-orthopedics-surgery',
];

const PRODUCT_INSTRUCTIONS_DEFER_THRESHOLD_BYTES = 90 * 1024;

const getJsonByteSize = (value: unknown) =>
  Buffer.byteLength(JSON.stringify(value ?? null), 'utf8');

const Slug = ({ data }: PageProps & LayoutProps): JSX.Element => {
  const [isMounted, setIsMounted] = useState(false);
  const [isConfirmationStored, setIsConfirmationStored] = useState(false);
  const [loadedInstructions, setLoadedInstructions] = useState<
    IProductInstructionsItem[]
  >([]);
  const router = useRouter();
  const { t } = useTranslation('common');
  const btnText = t('buttonMore');
  const btnConfirmText = t('confirmBtnText');
  const btnCancelText = t('cancelBtnText');
  const btnBuyText = t('buyBtnText');
  const confirmText = t('confirmText');
  const otherReleaseTitle = t('otherProductFormsReleaseTitle');
  const otherGroupTitle = t('otherProductReleaseGroupTitle');
  const valueToStore = data?.productsBy?.translatedSlug || '';
  //console.log(data, 'data');

  useEffect(() => {
    localStorage.setItem('translated_slug', valueToStore);
    return () => {
      localStorage.removeItem('translated_slug');
    };
  }, [valueToStore]);

  const {
    title,
    slug,
    featuredImage,
    categories,
    productCard,
    seo: seoChild,
    productInstructions,
    productSeoTextUa,
  } = data?.productsBy?.translation ?? {};

  const { aggregator } = productCard?.productInfo ?? {};
  const { seo: seoParent } = data?.pageBy?.translation ?? {};
  const featureImage = featuredImage?.node;
  const productInstructionsDeferred = productInstructions?.deferred ?? false;
  const instructions = productInstructionsDeferred
    ? loadedInstructions
    : productInstructions?.instructions ?? [];
  const seoText = productSeoTextUa?.seoText ?? '';
  const healthcareDocument =
    slug && PRODUCT_HEALTHCARE_DOCUMENTS_BY_SLUG[slug]
      ? PRODUCT_HEALTHCARE_DOCUMENTS_BY_SLUG[slug]
      : null;

  const description = productCard?.productInfo?.indications?.list
    ? productCard.productInfo.indications.list.map(item => item.text)
    : [''];

  const microMarkupSchema: microMarkupSchemaProps = {
    '@context': 'http://schema.org',
    '@type': 'Product',
    name: title,
    description: description.join(', '),
    image: featureImage?.mediaItemUrl,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '5.0',
      reviewCount: '7',
      ratingCount: '7',
      bestRating: '5',
      worstRating: '1',
      name: title,
    },
  };

  const breadcrumbs = [
    ...(seoParent?.breadcrumbs ?? []),
    {
      text: seoChild?.breadcrumbTitle,
      url: seoChild?.canonicalUrl,
    },
  ] as BreadcrumbsProps[];

  const categoryTerapevtGroup = categories?.nodes.filter(category =>
    slugsTerapevtGroup.includes(category.translation.slug),
  )[0]?.translation;

  const allProductsByTorgGroup = data?.relatedProductsByTorgGroup ?? [];
  const allProductsByTerapevtGroup = data?.relatedProductsByTerapevtGroup ?? [];

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    setLoadedInstructions(productInstructions?.instructions ?? []);
  }, [slug, productInstructions?.instructions]);

  useEffect(() => {
    if (!productInstructionsDeferred || !slug) {
      return;
    }

    const controller = new AbortController();

    fetch(
      `/api/product-instructions?slug=${encodeURIComponent(
        slug,
      )}&locale=${encodeURIComponent(router.locale ?? 'uk')}`,
      { signal: controller.signal },
    )
      .then(response => {
        if (!response.ok) {
          throw new Error('Unable to load product instructions');
        }

        return response.json();
      })
      .then((response: { instructions?: IProductInstructionsItem[] }) => {
        setLoadedInstructions(response.instructions ?? []);
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          setLoadedInstructions([]);
        }
      });

    return () => controller.abort();
  }, [productInstructionsDeferred, router.locale, slug]);

  useEffect(() => {
    const confirmation = localStorage.getItem('confirmation');
    if (confirmation === 'true') {
      setIsConfirmationStored(true);
    }
  }, []);

  return (
    <>
      <PageHead
        title={seoChild?.breadcrumbTitle || title}
        seo={seoParent || {}}
        canonicalUrl={seoChild?.canonicalUrl}
        microMarkupSchema={microMarkupSchema}
        breadcrumbs={breadcrumbs}
        descriptionChild={seoChild?.description}
      />
      {isMounted && (
        <>
          {!isConfirmationStored && (
            <Popup
              btnConfirmText={btnConfirmText}
              btnCancelText={btnCancelText}
              onConfirm={() => setIsConfirmationStored(true)}
            >
              <p
                dangerouslySetInnerHTML={{ __html: confirmText }}
                className="!mb-10"
              />
            </Popup>
          )}
          <Section
            className="pb-[60px] md:pb-[100px]"
            breadcrumbs={breadcrumbs}
            lazy={false}
          >
            <div className="grid gap-8 lg:grid-cols-2">
              <div className="max-w-[80%] md:max-w-[70%] lg:col-start-2 lg:max-w-[80%]">
                {categoryTerapevtGroup && (
                  <CategoryLabel
                    categoryName={categoryTerapevtGroup?.name}
                    color={categoryTerapevtGroup.category?.ategoryColor}
                    className="mb-5"
                  />
                )}
                {title && (
                  <Heading
                    tag="h1"
                    content={title}
                    className="md:h1Mobile text-h2Mobile"
                  />
                )}
              </div>
              <div className="min-h-fit lg:col-start-1 lg:row-span-3 lg:row-start-1 lg:pt-1">
                {productCard.gallery && productCard.gallery.length >= 2 ? (
                  <ProductGallerySlider gallery={productCard.gallery} />
                ) : (
                  <div className="flex aspect-[1.54/1] max-w-[460px] items-center justify-center bg-gradient-to-b from-grStart to-grEnd p-[38px] pt-[54px] md:p-[46px] lg:mx-0 lg:h-full lg:w-full lg:max-w-none ">
                    <Image
                      src={featureImage?.mediaItemUrl ?? '/images/image-bg.jpg'}
                      alt={featureImage?.altText ?? 'image not found'}
                      width={featureImage?.mediaDetails?.width ?? 288}
                      height={featureImage?.mediaDetails?.height ?? 194}
                      className="aspect-[1.54/1] object-contain object-center lg:max-h-[320px]"
                    />
                  </div>
                )}
              </div>

              {allProductsByTorgGroup.length > 0 && (
                <div className="md:row-start-2 lg:col-start-2">
                  <p className="mb-4 md:mb-5">{otherReleaseTitle}</p>
                  <ul className="grid max-w-[80%] gap-5 md:max-w-none md:gap-4">
                    {allProductsByTorgGroup.map(product => (
                      <li key={product.id}>
                        <Link
                          href={product?.slug}
                          prefetch={false}
                          className="flex items-baseline gap-4 transition-colors duration-300 hover:text-accentMain focus:text-accentMain"
                        >
                          <ArrowIcon className="h-3 w-3 shrink-0 text-accentMain" />
                          {product?.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {aggregator && (
                <ul className="flex max-w-[460px] flex-col gap-3 rounded-base p-5 shadow-accessibility md:row-start-3 lg:max-w-[67%]">
                  {aggregator.map(item => {
                    if (item.link && item.pharmacy) {
                      return (
                        <li
                          key={item?.link}
                          className="flex items-center justify-between gap-2 border-b border-primaryLight pb-3 last:border-none last:pb-0"
                        >
                          {item?.pharmacy}
                          <ButtonLink href={item?.link} isInnerLink={false}>
                            {btnBuyText}
                          </ButtonLink>
                        </li>
                      );
                    }
                  })}
                </ul>
              )}

              {productCard.productInfo && (
                <div className="lg:col-span-2 lg:row-start-4">
                  {Object.entries(productCard.productInfo)
                    .filter(([key]) => key !== '__typename')
                    .map(([key, product]) => {
                      const prodSubstance = product?.substance
                        ?.map((item: ISubstance) => item?.name)
                        .filter(Boolean);
                      return (
                        <React.Fragment key={key}>
                          {product?.buttonLink && product?.buttonText && (
                            <div className="mb-[60px] flex flex-col items-start gap-4 md:flex-row md:flex-wrap">
                              <ButtonLink
                                href={product.buttonLink.url}
                                isInnerLink={false}
                                withIcon
                              >
                                {product.buttonText}
                              </ButtonLink>
                              {key === 'instructions' && healthcareDocument && (
                                <ButtonLink
                                  className="min-h-12 h-auto max-w-full whitespace-normal text-center leading-5 md:h-12 md:leading-normal"
                                  href={healthcareDocument.href}
                                  isInnerLink={false}
                                  withIcon
                                >
                                  {healthcareDocument.label}
                                </ButtonLink>
                              )}
                            </div>
                          )}
                          {product?.title && product?.description && (
                            <p className="">
                              <span className="mr-1 font-semibold">
                                {product.title}:
                              </span>
                              {product.description}
                            </p>
                          )}
                          {product?.title &&
                            product?.substance &&
                            product?.substance.length > 0 && (
                              <p className="">
                                <span className="mr-1 font-semibold">
                                  {product.title}:
                                </span>
                                {prodSubstance.join(', ')}
                              </p>
                            )}
                          {product?.title && product?.list && (
                            <div className="mb-5 last:mb-0">
                              <p className="mr-1 font-semibold">
                                {product.title}:
                              </p>
                              <ul className="">
                                {product.list.map((item: IProductInfoList) => {
                                  return (
                                    <React.Fragment
                                      key={
                                        item.text ? item.text : item.link.url
                                      }
                                    >
                                      {item.text && (
                                        <li className="ml-3 list-['\2012'] pl-1 marker:text-accentMain">
                                          {item.text}
                                        </li>
                                      )}
                                      {item.link && (
                                        <li className="mb-5 last:mb-0">
                                          <a
                                            className="flex items-baseline gap-4 transition-colors duration-300 hover:text-accentMain focus:text-accentMain"
                                            href={item.link.url}
                                            target="_blank"
                                            rel="noopener noreferrer nofollow"
                                          >
                                            <ArrowIcon className="h-3 w-3 shrink-0 text-accentMain" />
                                            {item.link.title ||
                                              trimText(item.link.url, 50)}
                                          </a>
                                        </li>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  {instructions &&
                    instructions.length > 0 &&
                    instructions.map(
                      (item: IProductInstructionsItem, index: number) => (
                        <div className="mb-5" key={index}>
                          <p
                            className="!mb-1 mr-1 font-semibold"
                            dangerouslySetInnerHTML={{ __html: item.title }}
                          />

                          <p
                            className=""
                            dangerouslySetInnerHTML={{ __html: item.text }}
                          />
                        </div>
                      ),
                    )}
                </div>
              )}
            </div>
          </Section>
          {allProductsByTerapevtGroup &&
            allProductsByTerapevtGroup.length > 0 && (
              <Section className="py-10">
                <p className="relative mb-[60px] pb-7 text-h2Mobile font-bold before:absolute before:bottom-0 before:left-0 before:h-[1px] before:w-full before:bg-primaryLight before:content-[''] dark:text-darkFullLight md:mb-[80px] md:pb-10 md:text-h2Tablet lg:mb-[100px] lg:text-h2">
                  {otherGroupTitle}
                </p>
                <Slider
                  width={320}
                  desktopShowSlider={
                    allProductsByTerapevtGroup.length >= 3
                      ? 3
                      : allProductsByTerapevtGroup.length
                  }
                  tabletShowSlider={
                    allProductsByTerapevtGroup.length >= 2
                      ? 2
                      : allProductsByTerapevtGroup.length
                  }
                  withArrows
                  settings={{ autoplay: false, arrows: false }}
                >
                  {allProductsByTerapevtGroup.map(item => {
                    const { title, id, slug, featuredImage, categories } = item;

                    const categoryData = categories?.nodes.filter(category =>
                      slugsTerapevtGroup.includes(category.translation.slug),
                    );
                    return (
                      <div
                        key={id}
                        className={`p-2 ${
                          allProductsByTerapevtGroup.length < 3
                            ? 'md:max-w-[75%]'
                            : ''
                        }`}
                      >
                        <DrugCard
                          title={title}
                          buttonLabel={btnText}
                          drugImage={featuredImage?.node}
                          category={categoryData[0].translation}
                          linkUrl={removeLastTrailingSlash(slug)}
                          isInnerLink={true}
                          className="flex h-full flex-col content-between"
                          titleClassName="line-clamp-2"
                        />
                      </div>
                    );
                  })}
                </Slider>
              </Section>
            )}
          <Section className="py-10 !pt-0">
            {seoText && seoText.length > 0 && (
              <div
                className="bg-white p-8 pt-0 dark:bg-black"
                dangerouslySetInnerHTML={{
                  __html: seoText,
                }}
              ></div>
            )}
          </Section>
        </>
      )}
    </>
  );
};

export default withLayout(Slug);

//LABEL: PROPS
export const getStaticProps: GetStaticProps = async context => {
  const client = getApolloClient();

  const slug = `/${context.params?.slug ?? ROUTES.HOME}`;
  const language = context.locale?.toUpperCase();
  const allProductsLanguage = language;

  const location =
    context.locale == 'uk'
      ? MenuLocationEnum.PRIMARY_NAVIGATION
      : MenuLocationEnum.PRIMARY_NAVIGATION___EN;

  const slugLanguage = context.locale == 'uk' ? 'EN' : 'UK';

  const { data } = await client.query({
    query: PRODUCTS_BY_SLUG_QUERY,
    variables: {
      slug,
      language,
      allProductsLanguage,
    },
  });

  let translatedSlug = '';

  try {
    const { data: translatedData } = await client.query({
      query: PRODUCT_SLUG_TRANSLATION,
      variables: {
        slug,
        language: slugLanguage,
      },
    });

    translatedSlug =
      (translatedData?.productsBy?.translation?.slug as string) ?? '';
  } catch (e) {
    console.log('ERROR');
  }

  const productTranslation = data?.productsBy?.translation;
  const productInstructions = productTranslation?.productInstructions;
  const shouldDeferProductInstructions =
    getJsonByteSize(productInstructions) >
    PRODUCT_INSTRUCTIONS_DEFER_THRESHOLD_BYTES;
  const productTranslationForProps = productTranslation
    ? {
        ...productTranslation,
        productInstructions: shouldDeferProductInstructions
          ? {
              ...productInstructions,
              instructions: [],
              deferred: true,
            }
          : productInstructions,
      }
    : productTranslation;
  const allProductsList = data?.allProducts?.nodes ?? [];
  const categories = productTranslation?.categories;
  const categoryTerapevtGroup = categories?.nodes.filter(
    (category: { translation: { slug: string } }) =>
      slugsTerapevtGroup.includes(category.translation.slug),
  )[0]?.translation;
  const currentTorgGroup = categories?.nodes.filter(
    (category: { translation: { parent?: { node?: { slug?: string } } } }) =>
      category.translation.parent?.node?.slug === 'trade-name',
  )[0]?.translation;
  const relatedProductsByTorgGroup = filterProductsByCategory(
    allProductsList,
    currentTorgGroup?.slug,
  )
    .filter((product: IProduct) => product.id !== productTranslation?.id)
    .map((product: IProduct) => ({
      id: product.id,
      title: product.title,
      slug: product.slug,
    }));
  const relatedProductsByTerapevtGroup = filterProductsByCategory(
    allProductsList,
    categoryTerapevtGroup?.slug,
  )
    .filter((product: IProduct) => product.id !== productTranslation?.id)
    .map((product: IProduct) => ({
      id: product.id,
      title: product.title,
      slug: product.slug,
      featuredImage: product.featuredImage,
      categories: {
        nodes: product.categories.nodes.filter(category =>
          slugsTerapevtGroup.includes(category.translation.slug),
        ),
      },
    }));
  const dataWithoutAllProducts = { ...data };

  delete dataWithoutAllProducts.allProducts;

  const newData = {
    ...dataWithoutAllProducts,
    productsBy: {
      ...data.productsBy,
      translation: productTranslationForProps,
      translatedSlug,
    },
    relatedProductsByTorgGroup,
    relatedProductsByTerapevtGroup,
  };

  const {
    data: { menuItems },
  } = await client.query({
    query: ALL_MENU_QUERY,
    variables: {
      location,
    },
  });

  const footerLocation =
    context.locale == 'uk'
      ? FooterMenuLocationEnum.FOOTER_MENU
      : FooterMenuLocationEnum.FOOTER_MENU___EN;

  const { data: footerMenuItems } =
    context.locale == 'uk'
      ? await client.query({
          query: FOOTER_DATA_QUERY,
          variables: {
            location: footerLocation,
          },
        })
      : await client.query({
          query: FOOTER_DATA_QUERY_EN,
          variables: {
            location: footerLocation,
          },
        });
  const footerData = footerDataTransformer(footerMenuItems);

  const flatList = flatListToHierarchical(menuItems.nodes);
  const menus = transformMenus(flatList);

  return {
    props: {
      data: newData,
      menus,
      footerData,
      ...(await serverSideTranslations(context.locale as string, ['common'])),
    },
  };
};

//LABEL: PATHS
export const getStaticPaths = async ({ locales }: GetStaticPathsContext) => {
  const client = getApolloClient();

  const { data } = await client.query({
    query: ALL_PRODUCTS_PATH,
  });

  const paths = data.allProducts.nodes.flatMap(
    ({ slug, language }: { slug: string; language: { slug: string } }) => {
      return locales
        ?.filter(locale => locale === language.slug)
        .map(locale => ({
          params: {
            slug,
          },
          locale,
        }));
    },
  );

  return {
    paths,
    fallback: false,
  };
};
